<template>
    <div class="pe-0 pb-5">
        <LoaderComponent v-if="loading" />
        <div v-else>
            <div v-if="error" class="alert alert-danger">
                {{ error }}
            </div>
            <div v-else>
                <div class="row m-0">
                    <div class="col-12">
                        <div class="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pb-2 pt-2 mt-1 mb-1">
                            <h5 class="title mt-2">{{ $tl("labels.routes.booking.services.special") }}</h5>
                            <button
                                :disabled="!has_right_to_create_service"
                                type="button"
                                @click="openAddService"
                                class="btn btn-sm btn-success btn-circle">
                                <feather type="plus"></feather>
                                <span>{{ $tl("labels.booking.services.special.addShort") }}</span>
                            </button>
                        </div>
                    </div>
                </div>
                <div v-if="formErrors" class="alert alert-danger">
                    <p v-for="error in formErrors">
                        {{ error }}
                    </p>
                </div>

                <div v-if="formSuccess" class="alert alert-success">
                    {{ formSuccess }}
                </div>

                <div class="row m-0">
                    <div class="col-12">
                        <table v-if="services && services.length > 0" class="table table-sm table-striped border-bottom">
                            <thead class="border-bottom">
                                <tr>
                                    <th>{{ $tl("labels.form.name") }}</th>
                                    <th>{{ $tl("labels.booking.services.category") }}</th>
                                    <th>{{ $tl("labels.booking.services.special.datesShort") }}</th>
                                    <th>{{ $tl("labels.booking.services.pax") }}</th>
                                    <th class="text-center" v-tooltip="getTooltip($tl('labels.booking.services.showOnDashboardLong', restaurant_id))">
                                        {{ $tl("labels.booking.services.showOnDashboard") }}
                                    </th>
                                    <th>{{ $tl("labels.form.actions.title") }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="service in services" :key="service.id">
                                    <td>
                                        {{ service.name }}
                                    </td>
                                    <td style="text-transform: capitalize">
                                        {{ service.category }}
                                    </td>
                                    <td>
                                        <span v-if="areDatesSame(service.special_datetime_begin, service.special_datetime_end)">
                                            {{ displayDate(service.special_datetime_begin) }}
                                        </span>
                                        <span v-else>
                                            {{ $tl("labels.filters.dates.from") }}
                                            {{ displayDate(service.special_datetime_begin) }}
                                            {{ $tl("labels.filters.dates.to").toLowerCase() }}
                                            {{ displayDate(service.special_datetime_end) }}
                                        </span>
                                    </td>
                                    <td>
                                        {{ service.max_pax }}
                                    </td>
                                    <td class="text-center">
                                        <label class="form-label container-box me-0" style="width: initial">
                                            <input
                                                type="checkbox"
                                                v-model="service.show_on_dashboard"
                                                :disabled="!has_right_to_update_service || updatingShowOnDashboard.includes(service.id)"
                                                @change="toggleShowOnDashboard(service)" />
                                            <span
                                                class="checkmark me-1 mt-0"
                                                :class="{
                                                    disabled: !has_right_to_update_service || updatingShowOnDashboard.includes(service.id),
                                                }"></span>
                                        </label>
                                    </td>
                                    <td>
                                        <router-link
                                            :to="editServiceRoute(service)"
                                            class="btn btn-sm btn-success btn-square"
                                            v-tooltip="getTooltip($t('labels.form.actions.edit'))">
                                            <feather type="edit" />
                                        </router-link>
                                        <span class="none-mobile">
                                            <router-link
                                                :to="duplicateServiceRoute(service)"
                                                class="btn btn-sm btn-success btn-square"
                                                :class="{ disabled: !has_right_to_create_service }"
                                                v-tooltip="getTooltip($t('labels.form.actions.duplicate'))">
                                                <feather type="copy" />
                                            </router-link>
                                            <button
                                                v-if="!service.has_future_reservations"
                                                :disabled="!has_right_to_delete_service"
                                                @click="deleteService(service.id)"
                                                :data-id="service.id"
                                                class="btn btn-sm btn-danger btn-square"
                                                v-tooltip="getTooltip($t('labels.form.actions.delete'))">
                                                <feather type="trash-2" />
                                            </button>
                                            <button
                                                v-else
                                                class="btn btn-sm btn-secondary btn-square"
                                                v-tooltip="getTooltip($tl('infos.booking.services.cantDelete', restaurant_id))">
                                                <feather type="trash-2" />
                                            </button>
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div v-else>
                            <em>{{ $tl("labels.booking.services.special.empty", restaurant_id) }}</em>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <addServiceModal
            v-if="showAddServiceModal && !useServiceBuilderV2"
            :isSpecial="true"
            @close="showAddServiceModal = false"
            @service-added="fetchData" />
    </div>
</template>

<script>
import axios from "axios";
import ServicesDays from "../../../../components/services/ServiceDaysComponent";
import LoaderComponent from "../../../../components/LoaderComponent.vue";
import addServiceModal from "../../../../components/Modals/services/addServiceModal.vue";

export default {
    data() {
        return {
            loading: false,
            error: null,
            formLoading: null,
            formSuccess: null,
            formErrors: null,
            services: null,
            restaurant_id: null,
            restaurant: {
                id: null,
                services: null,
            },
            showAddServiceModal: false,
            updatingShowOnDashboard: [],
        };
    },
    computed: {
        rights: function () {
            return this.$store.getters["users/formattedRights"];
        },
        has_right_to_create_service() {
            return this.rights.includes("booking.service.create");
        },
        has_right_to_update_service() {
            return this.rights.includes("booking.service.update");
        },
        has_right_to_delete_service() {
            return this.rights.includes("booking.service.delete");
        },
        restaurantFromStore() {
            const restaurantId = this.restaurant_id || this.$route.params.restaurant_id;
            return this.$store.getters["restaurants/findRestaurantById"](restaurantId);
        },
        useServiceBuilderV2() {
            if (this.restaurant && Object.prototype.hasOwnProperty.call(this.restaurant, "feat_service_builder_v2")) {
                return !!this.restaurant.feat_service_builder_v2;
            }

            return this.restaurantFromStore && this.restaurantFromStore.feat_service_builder_v2;
        },
    },
    methods: {
        openAddService() {
            if (this.useServiceBuilderV2) {
                const restaurantId = this.restaurant_id || this.$route.params.restaurant_id;
                this.$router.push({
                    name: "booking.restaurants.settings.services.builder.add",
                    params: {
                        restaurant_id: restaurantId,
                    },
                    query: {
                        special: "1",
                    },
                });
                return;
            }
            this.showAddServiceModal = true;
        },
        fetchData() {
            this.error = this.restaurant = null;
            this.loading = true;
            this.restaurant_id = this.$route.params.restaurant_id;
            Promise.all([
                axios.get(`/api/restaurants/${this.restaurant_id}/services?specials=1`),
                axios.get(`/api/restaurants/${this.restaurant_id}?include=has_psp`),
            ])
                .then(([servicesResponse, restaurantResponse]) => {
                    this.services = servicesResponse.data.data;
                    this.restaurant = restaurantResponse.data?.data || restaurantResponse.data || null;
                    if (this.restaurant && this.restaurant.id) {
                        this.$store.commit("restaurants/updateById", this.restaurant);
                    }
                    this.loading = false;
                })
                .catch((error) => {
                    this.loading = false;
                    this.error = this.getErrorMsgFromErrorResponse(error);
                });
        },
        editServiceRoute(service) {
            if (this.useServiceBuilderV2) {
                return {
                    name: "booking.restaurants.settings.services.builder.edit",
                    params: {
                        restaurant_id: service.restaurant_id,
                        service_id: service.id,
                    },
                };
            }
            return {
                name: "booking.restaurants.settings.openings.edit",
                params: {
                    restaurant_id: service.restaurant_id,
                    service_id: service.id,
                },
            };
        },
        duplicateServiceRoute(service) {
            if (this.useServiceBuilderV2) {
                return {
                    name: "booking.restaurants.settings.services.builder.edit",
                    params: {
                        restaurant_id: service.restaurant_id,
                        service_id: service.id,
                    },
                    query: {
                        duplicate: "1",
                    },
                };
            }
            return {
                name: "booking.restaurants.settings.openings.add",
                params: {
                    restaurant_id: service.restaurant_id,
                    service_id: service.id,
                    isDuplicating: true,
                },
            };
        },
        deleteService(delete_service_id) {
            if (!confirm(this.$tl("questions.booking.services.special.delete"))) {
                return;
            }
            this.loading = true;

            axios
                .delete(`/api/restaurants/${this.restaurant_id}/services/${delete_service_id}`)
                .then(() => {
                    this.notifySuccess(null, this.$tl("success.booking.services.special.deleted", this.restaurant_id));

                    this.fetchData();
                })
                .catch((error) => {
                    this.loading = false;

                    this.notifyError(error);
                });
        },
        toggleShowOnDashboard(service) {
            if (!this.has_right_to_update_service || this.updatingShowOnDashboard.includes(service.id)) {
                return;
            }

            this.updatingShowOnDashboard.push(service.id);

            const toggle = service.show_on_dashboard ? "enable" : "disable";

            this.httpPut(`/api/restaurants/${this.restaurant_id}/services/showOnDashboard/${service.id}/${toggle}`, {}, { handleResolve: false })
                .then((response) => {
                    if (response === false) {
                        service.show_on_dashboard = !service.show_on_dashboard;
                    }
                })
                .finally(() => {
                    const index = this.updatingShowOnDashboard.findIndex((id) => id === service.id);

                    if (index !== -1) {
                        this.updatingShowOnDashboard.splice(index, 1);
                    }
                });
        },
    },
    created() {
        this.fetchData();
    },
    watch: {
        "$route.params.restaurant_id": function (id) {
            this.fetchData();
        },
    },
    components: {
        ServicesDays,
        LoaderComponent,
        addServiceModal,
    },
};
</script>
